import React, { useState, useEffect } from 'react';
 
import { 
  LuUsers, 
  LuUserPlus, 
  LuSearch, 
  LuEllipsisVertical, 
  LuPen, 
  LuTrash2, 
  LuEye, 
  LuEyeOff,
  LuMail, 
  LuPhone, 
  LuX,
  LuSave,
  LuUser,
  LuShield,
  LuCrown,
  LuUserCheck,
  LuClock,
  LuCalendar,
  LuChartBar,
  LuLogIn,
  LuLogOut,
  LuSettings,
  LuChevronDown,
  LuRotateCcw,
  LuCheck
} from 'react-icons/lu';

import axios from 'axios';
import BASE_URL from '../../url';
import '../styles/UserManagement.css';

// Catalog of all sidebar menus & submenus available in the ERP
const SIDEBAR_MENUS_CATALOG = [
  {
    category: "Core & General",
    items: [
      { key: "timesheets", label: "My Timesheets", desc: "View and log internal timesheets" },
      { key: "leave_manage", label: "Leave Manage", desc: "Leave applications and calendar" },
      { key: "track_status", label: "Profile / Track Status", desc: "Employee profile & candidate tracking" }
    ]
  },
  {
    category: "Recruitment",
    items: [
      { key: "recruitment_dashboard", label: "Recruitment Dashboard", desc: "Recruiter / Team Lead / Manager overview" }
    ]
  },
  {
    category: "Job Submissions",
    items: [
      { key: "submissions", label: "Submissions Dashboard", desc: "Active candidate submissions" },
      { key: "job_report", label: "Submission Report", desc: "Detailed submission reporting" },
      { key: "job_count_report", label: "Job Report", desc: "Open job counts and statuses" },
      { key: "client_count", label: "Client Report", desc: "Client breakdown reports" },
      { key: "performance_report", label: "Performance Report", desc: "Recruiter performance analytics" }
    ]
  },
  {
    category: "Candidate Management",
    items: [
      { key: "candidate_sourcing", label: "Sourcing", desc: "Resume sourcing and discovery" },
      { key: "candidate_bench", label: "Bench Candidates", desc: "Bench profile listings" },
      { key: "candidate_review", label: "Need To Review", desc: "Resumes awaiting review" }
    ]
  },
  {
    category: "Bench Manage",
    items: [
      { key: "bench_candidates_list", label: "Bench Candidates", desc: "Bench candidate pool" },
      { key: "bench_dashboard", label: "Bench Dashboard", desc: "Bench sales dashboard overview" },
      { key: "bench_submissions", label: "Bench Submissions", desc: "Bench marketing submissions" },
      { key: "bench_reports", label: "Bench Report", desc: "Bench sales analytics" },
      { key: "bench_heat_report", label: "Bench Heat Report", desc: "Market hotlist and heat map" }
    ]
  },
  {
    category: "CRM (Customer Relationship)",
    items: [
      { key: "crm_companies", label: "Vendor & Client", desc: "Company and client directory" },
      { key: "crm_msa_po", label: "MSA & PO", desc: "Master agreements & purchase orders" }
    ]
  },
  {
    category: "HRM & Onboarding",
    items: [
      { key: "hrm_accounting", label: "Accounting", desc: "Payroll and finance accounts" },
      { key: "hrm_onboarding", label: "Onboarding Dashboard", desc: "Employee onboarding tracking" },
      { key: "hrm_onboarding_report", label: "Onboarding Report", desc: "Detailed onboarding status reports" },
      { key: "deployment_report", label: "Deployment Report", desc: "Deployment & client project report" }
    ]
  },
  {
    category: "ATS & Visas",
    items: [
      { key: "ats", label: "ATS Module", desc: "Applicant Tracking System" },
      { key: "h1b", label: "H1B Management", desc: "H1B visa filing and tracking" }
    ]
  },
  {
    category: "Administration",
    items: [
      { key: "user_management", label: "User Management", desc: "Manage ERP user accounts & roles" },
      { key: "invoices", label: "Invoices", desc: "Invoicing and billing management" },
      { key: "timesheet_approvals", label: "Timesheet Approvals", desc: "Approve subordinate timesheets" }
    ]
  }
];

// Fallback defaults matching current hardcoded system behavior
const DEFAULT_ROLE_PERMISSIONS = {
  admin: [
    "timesheets", "leave_manage", "track_status", "recruitment_dashboard",
    "submissions", "job_report", "job_count_report", "client_count", "performance_report",
    "candidate_sourcing", "candidate_bench", "candidate_review",
    "bench_candidates_list", "bench_dashboard", "bench_submissions", "bench_reports", "bench_heat_report",
    "crm_companies", "crm_msa_po",
    "hrm_accounting", "hrm_onboarding", "hrm_onboarding_report", "deployment_report",
    "ats", "h1b",
    "user_management", "invoices", "timesheet_approvals"
  ],
  manager: [
    "timesheets", "leave_manage", "recruitment_dashboard",
    "submissions", "job_report", "job_count_report", "client_count", "performance_report",
    "candidate_sourcing", "candidate_bench", "candidate_review",
    "bench_candidates_list", "bench_dashboard", "bench_submissions", "bench_reports", "bench_heat_report",
    "ats", "timesheet_approvals"
  ],
  teamlead: [
    "timesheets", "leave_manage", "recruitment_dashboard",
    "submissions", "job_report", "job_count_report", "client_count", "performance_report",
    "candidate_sourcing", "candidate_bench", "candidate_review",
    "bench_candidates_list", "bench_dashboard", "bench_submissions", "bench_reports", "bench_heat_report",
    "ats", "timesheet_approvals"
  ],
  employee: [
    "track_status", "timesheets", "leave_manage"
  ],
  user: [
    "timesheets", "leave_manage", "recruitment_dashboard",
    "submissions", "job_report", "job_count_report", "client_count", "performance_report",
    "candidate_sourcing", "candidate_bench", "candidate_review",
    "bench_candidates_list", "bench_dashboard", "bench_submissions", "bench_reports", "bench_heat_report",
    "ats"
  ],
  external: [
    "ats"
  ]
};

const UserManagement = () => {
   
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Add state for session data
  const [userSessions, setUserSessions] = useState({});
  const [sessionStats, setSessionStats] = useState({});
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [selectedUserForSessions, setSelectedUserForSessions] = useState(null);
  const [sessionPeriod, setSessionPeriod] = useState('daily');
  
  // Password visibility states
  const [showAddPassword, setShowAddPassword] = useState(false);
  const [showEditPassword, setShowEditPassword] = useState(false);

  // Role Menu Permissions state
  const [editRoleDropdownOpen, setEditRoleDropdownOpen] = useState(false);
  const [addRoleDropdownOpen, setAddRoleDropdownOpen] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedRoleForPerms, setSelectedRoleForPerms] = useState(null);
  const [selectedUserForPerms, setSelectedUserForPerms] = useState(null);
  const [currentPermissions, setCurrentPermissions] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const [allRolePermissions, setAllRolePermissions] = useState({});
  const [toastMessage, setToastMessage] = useState(null);

  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    role: 'user',
    password: '',
    employeeId: ''
  });

  const roles = [
    { value: 'admin', label: 'Admin', icon: <LuShield />, color: '#dc3545' },
    { value: 'manager', label: 'Manager', icon: <LuCrown />, color: '#0eb381ff' },
    { value: 'teamlead', label: 'Team Lead', icon: <LuUserCheck />, color: '#ffc107' },
    { value: 'employee', label: 'employee', icon: <LuUser />, color: '#17a2b8' },
    { value: 'user', label: 'User', icon: <LuUser />, color: '#6c757d' },
    { value: 'external', label: 'External User', icon: <LuUser />, color: '#9b59b6' }
  ];

  const statuses = [
    { value: 'active', label: 'Active', color: '#20c997' },
    { value: 'inactive', label: 'Inactive', color: '#de6b76ff' }
  ];

  // Fetch role permissions on load
  useEffect(() => {
    const fetchRolePermissions = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/api/auth/role-permissions`, {
          params: { _t: Date.now() },
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        if (response.data && response.data.permissions) {
          setAllRolePermissions(response.data.permissions);
          localStorage.setItem('erp_role_menu_permissions', JSON.stringify(response.data.permissions));
        }
      } catch (err) {
        console.warn("Could not fetch remote role permissions:", err);
        const cached = localStorage.getItem('erp_role_menu_permissions');
        if (cached) {
          try {
            setAllRolePermissions(JSON.parse(cached));
          } catch (e) {}
        }
      }
    };
    fetchRolePermissions();
  }, []);

  // Fetch users from the existing /api/auth/users endpoint
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${BASE_URL}/api/auth/users`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log("Fetched users:", response.data);
        
        // Handle both possible response formats
        const userData = response.data.users || response.data || [];
        
        // SET USERS FIRST
        setUsers(userData);
        setFilteredUsers(userData);
        setLoading(false);

        console.log("=== USER DATA DEBUG ===");
        userData.forEach(user => {
          console.log(`User: ${user.firstName} ${user.lastName}`, {
            id: user.id,
            lastLogin: user.lastLogin,
            lastLogout: user.lastLogout,
            hasLastLogin: !!user.lastLogin,
            hasLastLogout: !!user.lastLogout
          });
        });
        console.log("=== END DEBUG ===");
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Failed to load users");
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Fetch session stats for visible page users to avoid flooding the server with hundreds of concurrent requests
  useEffect(() => {
    const fetchSessionStatsForUsers = async () => {
      const startIdx = (currentPage - 1) * usersPerPage;
      const endIdx = startIdx + usersPerPage;
      const visibleUsers = (filteredUsers.length > 0 ? filteredUsers.slice(startIdx, endIdx) : users.slice(startIdx, endIdx))
        .filter(u => u && u.id && !sessionStats[u.id]);

      if (visibleUsers.length === 0) return;

      try {
        const token = localStorage.getItem('token');
        const statsPromises = visibleUsers.map(async (user) => {
          try {
            const response = await axios.get(
              `${BASE_URL}/api/auth/user-session-stats/${user.id}?period=monthly`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            return { userId: user.id, stats: response.data };
          } catch (err) {
            return { userId: user.id, stats: { totalHours: 0 } };
          }
        });

        const statsResults = await Promise.all(statsPromises);
        setSessionStats(prev => {
          const updated = { ...prev };
          statsResults.forEach(r => {
            if (r && r.userId) updated[r.userId] = r.stats;
          });
          return updated;
        });
      } catch (error) {
        // Safe fallback
      }
    };

    fetchSessionStatsForUsers();
  }, [users, currentPage, filteredUsers]);

  // Filter users
  useEffect(() => {
    let filtered = users;

    if (searchTerm) {
      filtered = filtered.filter(user =>
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.phone && user.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.mobile && user.mobile.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    if (selectedRole !== 'all') {
      filtered = filtered.filter(user => user.role === selectedRole);
    }

    // For status filtering
    if (selectedStatus !== 'all') {
      filtered = filtered.filter(user => user.status === selectedStatus);
    }

    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [users, searchTerm, selectedRole, selectedStatus]);

  const getRoleInfo = (role) => {
    return roles.find(r => r.value === role) || roles[4]; // Default to 'user'
  };

  const getStatusColor = (status) => {
    return statuses.find(s => s.value === status)?.color || '#20c997';
  };

  const fetchUserSessionStats = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/auth/user-session-stats/${userId}?period=${sessionPeriod}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSessionStats(prev => ({
        ...prev,
        [userId]: response.data
      }));
    } catch (err) {
      console.error("Error fetching session stats:", err);
    }
  };

  // Add function to open session details modal
  const openSessionModal = async (user) => {
    setSelectedUserForSessions(user);
    setSessionModalOpen(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/auth/user-sessions/${user.id}?period=${sessionPeriod}&limit=50`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Filter sessions based on the selected period
      const filteredSessions = filterSessionsByPeriod(response.data.sessions || [], sessionPeriod);
      
      setUserSessions(prev => ({
        ...prev,
        [user.id]: {
          ...response.data,
          sessions: filteredSessions
        }
      }));
    } catch (err) {
      console.error("Error fetching session details:", err);
    }
  };

  // Add this helper function to filter sessions by period
  const filterSessionsByPeriod = (sessions, period) => {
    const now = new Date();
    let startDate;

    switch (period) {
      case 'daily':
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
        break;
      case 'weekly':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        break;
      case 'monthly':
        startDate = new Date(now);
        startDate.setMonth(now.getMonth() - 1);
        break;
      default:
        return sessions;
    }

    return sessions.filter(session => {
      const sessionDate = new Date(session.login_time || session.createdAt);
      return sessionDate >= startDate;
    });
  };

  useEffect(() => {
    if (sessionModalOpen && selectedUserForSessions) {
      openSessionModal(selectedUserForSessions);
    }
  }, [sessionPeriod]);

  // Add new user - Fixed to use correct endpoint
  const handleAddUser = async () => {
    if (!newUser.firstName || !newUser.email) {
      alert('Please fill in all required fields (First Name, Email)');
      return;
    }

    if (!newUser.password || newUser.password.trim() === '') {
      alert('Please enter a password');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      alert('Please enter a valid email address');
      return;
    }
    
    try {
      // All roles use email as username
      const username = newUser.email.trim().toLowerCase();
      
      const userData = {
        firstName: newUser.firstName.trim(),
        lastName: newUser.lastName?.trim() || '',
        email: newUser.email.trim().toLowerCase(),
        mobile: newUser.mobile?.trim() || '',
        role: newUser.role,
        username: username, // Employee: email, Others: firstName
        password: newUser.password.trim(),
        employeeId: newUser.employeeId?.trim() || ''
      };
      
      const token = localStorage.getItem('token');
      
      // Use the correct endpoint that we created
      const response = await axios.post(`${BASE_URL}/api/auth/create-user`, userData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log("User created successfully:", response.data);

      // Refresh users list
      const usersResponse = await axios.get(`${BASE_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const updatedUsers = usersResponse.data.users || usersResponse.data || [];
      setUsers(updatedUsers);
      
      // Reset form
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        mobile: '',
        role: 'user',
        password: ''
      });
      
      setShowAddModal(false);
      alert(`User added successfully! They can now login with username: ${username}`);
      
    } catch (err) {
      console.error("Error adding user:", err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to add user. Please try again.';
      alert(errorMessage);
    }
  };

  // Enhanced Edit user with role and password support
  const handleEditUser = async () => {
    if (!selectedUser) return;

    if (!selectedUser.firstName || !selectedUser.email) {
      alert('Please fill in all required fields (First Name, Email)');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(selectedUser.email)) {
      alert('Please enter a valid email address');
      return;
    }

    try {
      const userData = {
        firstName: selectedUser.firstName.trim(),
        lastName: selectedUser.lastName?.trim() || '',
        email: selectedUser.email.trim().toLowerCase(),
        mobile: selectedUser.mobile?.trim() || '',
        role: selectedUser.role, // Include role in update
        employeeId: selectedUser.EmployeeId || selectedUser.employeeId || '', // Sync EmployeeId
      };

      // Include password only if it's been changed
      if (selectedUser.password && selectedUser.password.trim()) {
        userData.password = selectedUser.password.trim();
      }

      const token = localStorage.getItem('token');
      
      // Use existing update endpoint
      await axios.put(`${BASE_URL}/api/auth/user/${selectedUser.id}`, userData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Refresh users list
      const usersResponse = await axios.get(`${BASE_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const updatedUsers = usersResponse.data.users || usersResponse.data || [];
      setUsers(updatedUsers);
      setShowEditModal(false);
      setSelectedUser(null);
      alert('User updated successfully!');
      
    } catch (err) {
      console.error("Error updating user:", err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to update user. Please try again.';
      alert(errorMessage);
    }
  };

  const toggleDropdown = (userId) => {
    setDropdownOpen(dropdownOpen === userId ? null : userId);
  };

  const openEditModal = (user) => {
    setSelectedUser({ 
      ...user,
      password: '', // Initialize password field as empty
      employeeId: user.EmployeeId || user.employeeId || ''
    });
    setShowEditModal(true);
    setDropdownOpen(null);
  };

  const handleDeleteUser = async (user) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to permanently delete ${user.firstName} ${user.lastName}?\n\nThis action cannot be undone!`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${BASE_URL}/api/auth/user/${user.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh users list
      const usersResponse = await axios.get(`${BASE_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const updatedUsers = usersResponse.data.users || usersResponse.data || [];
      setUsers(updatedUsers);
      setDropdownOpen(null);
      alert(`User ${user.firstName} ${user.lastName} has been permanently deleted.`);
      
    } catch (err) {
      console.error("Error deleting user:", err);
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error || 
                          'Failed to delete user. Please try again.';
      alert(errorMessage);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewUser({
      firstName: '',
      lastName: '',
      email: '',
      mobile: '',
      role: 'user',
      employeeId: '',
      password: ''
    });
    setShowAddPassword(false);
    setAddRoleDropdownOpen(false);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setShowEditPassword(false);
    setEditRoleDropdownOpen(false);
  };

  // Open user permissions configuration modal for an individual person
  const handleOpenUserMenuPermissions = async (user) => {
    setDropdownOpen(null);
    setSelectedUserForPerms(user);
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || `User #${user.id}`;
    setSelectedRoleForPerms({
      value: (user.role || 'user').toLowerCase().trim(),
      label: `${fullName} (${user.email || user.role})`
    });

    let perms = [];
    try {
      const response = await axios.get(`${BASE_URL}/api/auth/user-permissions/${user.id}?_t=${Date.now()}`);
      if (response.data && Array.isArray(response.data.permissions)) {
        perms = response.data.permissions;
      }
    } catch (e) {
      console.warn("Could not fetch user permissions:", e);
    }

    // Fall back to role permissions if no custom user permissions are configured yet
    if (perms.length === 0) {
      const roleKey = (user.role || 'user').toLowerCase().trim();
      if (allRolePermissions && Array.isArray(allRolePermissions[roleKey])) {
        perms = [...allRolePermissions[roleKey]];
      } else if (DEFAULT_ROLE_PERMISSIONS[roleKey]) {
        perms = [...DEFAULT_ROLE_PERMISSIONS[roleKey]];
      }
    }

    setCurrentPermissions(perms);
    setPermissionsModalOpen(true);
  };

  // Open role permissions configuration modal
  const handleOpenRolePermissions = (role) => {
    setSelectedUserForPerms(null);
    setEditRoleDropdownOpen(false);
    setAddRoleDropdownOpen(false);
    setSelectedRoleForPerms(role);
    let perms = [];
    if (allRolePermissions && Array.isArray(allRolePermissions[role.value])) {
      perms = [...allRolePermissions[role.value]];
    } else {
      const cached = localStorage.getItem('erp_role_menu_permissions');
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed && Array.isArray(parsed[role.value])) {
            perms = [...parsed[role.value]];
          }
        } catch (e) {}
      }
    }

    if (perms.length === 0 && DEFAULT_ROLE_PERMISSIONS[role.value]) {
      perms = [...DEFAULT_ROLE_PERMISSIONS[role.value]];
    }

    setCurrentPermissions(perms);
    setPermissionsModalOpen(true);
  };

  // Get active menu catalog merging predefined with dynamic newly detected menus
  const getActiveCatalog = () => {
    try {
      const stored = localStorage.getItem('erp_dynamic_sidebar_menus');
      if (!stored) return SIDEBAR_MENUS_CATALOG;
      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed) || parsed.length === 0) return SIDEBAR_MENUS_CATALOG;

      const knownKeys = new Set();
      SIDEBAR_MENUS_CATALOG.forEach(cat => cat.items.forEach(i => knownKeys.add(i.key)));

      const extraItems = [];
      parsed.forEach(cat => {
        cat.items.forEach(item => {
          if (!knownKeys.has(item.key) && !extraItems.some(e => e.key === item.key)) {
            extraItems.push(item);
          }
        });
      });

      if (extraItems.length === 0) return SIDEBAR_MENUS_CATALOG;

      return [
        ...SIDEBAR_MENUS_CATALOG,
        {
          category: "Newly Added Sidebar Menus",
          items: extraItems
        }
      ];
    } catch (e) {
      return SIDEBAR_MENUS_CATALOG;
    }
  };

  // Toggle individual menu key
  const handleTogglePermission = (menuKey) => {
    setCurrentPermissions(prev => 
      prev.includes(menuKey) ? prev.filter(k => k !== menuKey) : [...prev, menuKey]
    );
  };

  // Select all menus in catalog
  const handleSelectAllPermissions = () => {
    const allKeys = [];
    getActiveCatalog().forEach(cat => {
      cat.items.forEach(item => allKeys.push(item.key));
    });
    setCurrentPermissions(allKeys);
  };

  // Deselect all
  const handleDeselectAllPermissions = () => {
    setCurrentPermissions([]);
  };

  // Reset to default configuration for this role
  const handleResetToDefaultPermissions = () => {
    if (!selectedRoleForPerms) return;
    const roleKey = selectedUserForPerms ? (selectedUserForPerms.role || 'user').toLowerCase().trim() : selectedRoleForPerms.value;
    const defaults = DEFAULT_ROLE_PERMISSIONS[roleKey] || [];
    setCurrentPermissions([...defaults]);
  };

  // Save permissions to DB & localStorage (Async – waits for DB confirmation before closing modal)
  const handleSaveRolePermissions = async () => {
    if (!selectedRoleForPerms) return;
    setSavingPermissions(true);

    const token = localStorage.getItem('token');

    if (selectedUserForPerms) {
      const targetUserId = selectedUserForPerms.id;
      const userName = `${selectedUserForPerms.firstName || ''} ${selectedUserForPerms.lastName || ''}`.trim() || selectedUserForPerms.username;

      try {
        // 1. Save to database first – wait for confirmation
        await axios.post(`${BASE_URL}/api/auth/user-permissions`, {
          userId: targetUserId,
          permissions: currentPermissions
        }, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        // 2. Only after successful DB save: update local state & storage
        const currentLoggedInId = localStorage.getItem('id');
        if (currentLoggedInId && String(currentLoggedInId) === String(targetUserId)) {
          localStorage.setItem('erp_user_menu_permissions', JSON.stringify(currentPermissions));
          window.dispatchEvent(new Event('rolePermissionsUpdated'));
        }

        // 3. Broadcast to the user's other open tabs
        try {
          if (typeof BroadcastChannel !== 'undefined') {
            const bc = new BroadcastChannel('erp_role_permissions_channel');
            bc.postMessage({
              type: 'USER_PERMISSIONS_UPDATED',
              userId: targetUserId,
              permissions: currentPermissions
            });
            bc.close();
          }
        } catch (e) {}

        // 4. Close modal & show success toast
        setPermissionsModalOpen(false);
        setSelectedUserForPerms(null);
        setSavingPermissions(false);
        setToastMessage(`Menu permissions for "${userName}" saved successfully!`);
        setTimeout(() => setToastMessage(null), 3500);

      } catch (err) {
        // DB save failed – keep modal open so user can retry
        setSavingPermissions(false);
        const errMsg = err?.response?.data?.error || err?.response?.data?.details || err?.message || 'Unknown error';
        setToastMessage(`❌ Failed to save permissions: ${errMsg}`);
        setTimeout(() => setToastMessage(null), 5000);
        console.error("User permissions save error:", err);
      }
      return;
    }

    const roleKey = selectedRoleForPerms.value;
    const roleLabel = selectedRoleForPerms.label;

    try {
      // 1. Save to database first – wait for confirmation
      await axios.post(`${BASE_URL}/api/auth/role-permissions`, {
        role: roleKey,
        permissions: currentPermissions
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      // 2. Only after successful DB save: update local state & storage
      const updatedMap = {
        ...allRolePermissions,
        [roleKey]: currentPermissions
      };
      setAllRolePermissions(updatedMap);
      localStorage.setItem('erp_role_menu_permissions', JSON.stringify(updatedMap));

      // 3. Dispatch global event so Sidebar updates instantly in current window & all tabs
      window.dispatchEvent(new Event('rolePermissionsUpdated'));
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          const bc = new BroadcastChannel('erp_role_permissions_channel');
          bc.postMessage({ type: 'PERMISSIONS_UPDATED', permissions: updatedMap });
          bc.close();
        }
      } catch (e) {}

      // 4. Close modal & show success toast
      setPermissionsModalOpen(false);
      setSelectedUserForPerms(null);
      setSavingPermissions(false);
      setToastMessage(`Menu permissions for "${roleLabel}" saved successfully!`);
      setTimeout(() => {
        setToastMessage(null);
      }, 3500);

    } catch (err) {
      // DB save failed – keep modal open so user can retry
      setSavingPermissions(false);
      const errMsg = err?.response?.data?.error || err?.response?.data?.details || err?.message || 'Unknown error';
      setToastMessage(`❌ Failed to save permissions: ${errMsg}`);
      setTimeout(() => setToastMessage(null), 5000);
      console.error("Role permissions save error:", err);
    }
  };

  // Pagination logic
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return <div className="user-management-container">Loading users...</div>;
  if (error) return <div className="user-management-container">Error: {error}</div>;

  return (
    <div className="user-management-container">
      {/* Header */}
      <div className="user-management-header">
        <div className="user-management-header-left">
          <LuUsers className="user-management-header-icon" />
          <h1 className="user-management-title">User Management</h1>
          <span className="user-management-count">{filteredUsers.length} users</span>
        </div>
        <button className="user-management-add-button" onClick={() => setShowAddModal(true)}>
          <LuUserPlus />
          Add New User
        </button>
      </div>

      {/* Filters */}
      <div className="user-management-filters-container">
        <div className="user-management-search-container">
          <LuSearch className="user-management-search-icon" />
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="user-management-search-input"
          />
        </div>
        <div className="user-management-filter-group">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="user-management-filter-select"
          >
            <option value="all">All Roles</option>
            {roles.map(role => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="user-management-filter-select"
          >
            <option value="all">All Status</option>
            {statuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="user-management-table-container">
        <table className="user-management-table">
          <thead className="user-management-table-header">
            <tr>
              <th className="user-management-th">User</th>
              <th className="user-management-th">Contact</th>
              <th className="user-management-th">Role</th>
              <th className="user-management-th">Status</th>
              <th className="user-management-th">Last Login</th>
              <th className="user-management-th">Last Logout</th>
              <th className="user-management-th">Total Hours</th>
              <th className="user-management-th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentUsers.map((user, index) => (
              <tr key={user.id} className="user-management-table-row">
                <td className="user-management-td">
                  <div className="user-management-user-cell">
                    <div className="user-management-avatar">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </div>
                    <div>
                      <div className="user-management-user-name">{user.firstName} {user.lastName}</div>
                      <div className="user-management-user-email">@{user.username}</div>
                    </div>
                  </div>
                </td>
                <td className="user-management-td">
                  <div className="user-management-contact-info">
                    <div className="user-management-contact-item">
                      <LuMail size={12} />
                      {user.email}
                    </div>
                    {user.mobile && (
                      <div className="user-management-contact-item">
                        <LuPhone size={12} />
                        Mobile: {user.mobile}
                      </div>
                    )}
                  </div>
                </td>
                <td className="user-management-td">
                  <div className="user-management-role-cell">
                    <span className="user-management-role-icon" style={{color: getRoleInfo(user.role).color}}>
                      {getRoleInfo(user.role).icon}
                    </span>
                    {getRoleInfo(user.role).label}
                  </div>
                </td>
                <td className="user-management-td">
                  <span className="user-management-status-badge" style={{backgroundColor: getStatusColor(user.status || 'active')}}>
                    {user.status || 'Active'}
                  </span>
                </td>
                <td className="user-management-td">
                  <div className="session-tracker-cell">
                    <LuLogIn size={14} className="session-login-icon" />
                    <span className="session-time-display">
                      {user.lastLogin 
                        ? `${new Date(user.lastLogin).toLocaleDateString()} ${new Date(user.lastLogin).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                        : 'Never'
                      }
                    </span>
                  </div>
                </td>
                <td className="user-management-td">
                  <div className="session-tracker-cell">
                    <LuLogOut size={14} className="session-logout-icon" />
                    <span className="session-time-display">
                      {user.lastLogout 
                        ? `${new Date(user.lastLogout).toLocaleDateString()} ${new Date(user.lastLogout).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}`
                        : user.lastLogin ? 'Active' : 'Never'
                      }
                    </span>
                  </div>
                </td>
                <td className="user-management-td">
                  <div className="session-tracker-cell">
                    <LuClock size={14} className="session-hours-icon" />
                    <span className="session-total-hours">
                      {sessionStats[user.id]?.totalHours?.toFixed(1) || '0.0'} hrs
                    </span>
                  </div>
                </td>
                <td className="user-management-td">
                  <div className="user-management-actions-container">
                    <button
                      className="user-management-action-button"
                      onClick={() => toggleDropdown(user.id)}
                    >
                      <LuEllipsisVertical />
                    </button>
                    {dropdownOpen === user.id && (
                      <>
                        <div className="user-management-dropdown-overlay" onClick={() => setDropdownOpen(null)}></div>
                        <div className={`user-management-dropdown ${index >= currentUsers.length - 2 ? 'dropdown-up' : ''}`}>
                        <button
                          className="user-management-dropdown-item"
                          onClick={() => openEditModal(user)}
                        >
                          <LuPen /> Edit
                        </button>
                        <button
                          className="user-management-dropdown-item"
                          onClick={() => handleOpenUserMenuPermissions(user)}
                        >
                          <LuSettings /> Configure Menu Access
                        </button>
                        <button
                          className="user-management-dropdown-item"
                          onClick={() => openSessionModal(user)}
                        >
                          <LuChartBar /> Session Details
                        </button>
                        <button
                          className="user-management-dropdown-item"
                          onClick={() => alert(`Viewing ${user.firstName} ${user.lastName}\nEmail: ${user.email}\nRole: ${user.role}\nPhone: ${user.phone || 'N/A'}\nMobile: ${user.mobile || 'N/A'}`)}
                        >
                          <LuEye /> View Details
                        </button>
                        <button
                          className="user-management-dropdown-item user-management-dropdown-delete"
                          onClick={() => handleDeleteUser(user)}
                        >
                          <LuTrash2 /> Delete
                        </button>
                      </div>
                    </>
                  )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginTop: '20px', 
          padding: '16px 24px',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
          border: '1px solid #f0f0f0',
          marginBottom: '0px'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
            Showing <span style={{color: '#111827', fontWeight: '600'}}>{indexOfFirstUser + 1}</span> to <span style={{color: '#111827', fontWeight: '600'}}>{Math.min(indexOfLastUser, filteredUsers.length)}</span> of <span style={{color: '#111827', fontWeight: '600'}}>{filteredUsers.length}</span> users
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button 
              disabled={currentPage === 1}
              onClick={() => paginate(currentPage - 1)}
              style={{ 
                padding: '8px 16px', 
                borderRadius: '20px', 
                border: '1px solid #e5e7eb', 
                backgroundColor: currentPage === 1 ? '#f9fafb' : 'white', 
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '14px',
                transition: 'all 0.2s',
                fontFamily: 'inherit'
              }}
            >
              Previous
            </button>
            
            <div style={{ display: 'flex', gap: '4px', margin: '0 4px' }}>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(number => {
                  if (totalPages <= 5) return true;
                  if (number === 1 || number === totalPages) return true;
                  if (number >= currentPage - 1 && number <= currentPage + 1) return true;
                  return false;
                })
                .map((number, index, array) => {
                  const isGap = index > 0 && number - array[index - 1] > 1;
                  return (
                    <React.Fragment key={number}>
                      {isGap && <span style={{ display: 'flex', alignItems: 'flex-end', padding: '0 4px', color: '#9ca3af', userSelect: 'none' }}>...</span>}
                      <button
                        onClick={() => paginate(number)}
                        style={{
                          width: '36px',
                          height: '36px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '50%',
                          border: 'none',
                          backgroundColor: currentPage === number ? '#0eb381' : 'transparent',
                          color: currentPage === number ? 'white' : '#4b5563',
                          fontWeight: currentPage === number ? '600' : '500',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          fontFamily: 'inherit'
                        }}
                      >
                        {number}
                      </button>
                    </React.Fragment>
                  );
                })}
            </div>
            
            <button 
              disabled={currentPage === totalPages}
              onClick={() => paginate(currentPage + 1)}
              style={{ 
                padding: '8px 16px', 
                borderRadius: '20px', 
                border: '1px solid #e5e7eb', 
                backgroundColor: currentPage === totalPages ? '#f9fafb' : 'white', 
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontWeight: '500',
                fontSize: '14px',
                transition: 'all 0.2s',
                fontFamily: 'inherit'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Session Modal */}
      {sessionModalOpen && selectedUserForSessions && (
        <div className="user-management-modal-overlay">
          <div className="user-management-modal session-analytics-modal">
            <div className="user-management-modal-header">
              <h2 className="user-management-modal-title">
                Session Details - {selectedUserForSessions.firstName} {selectedUserForSessions.lastName}
              </h2>
              <button className="user-management-close-button" onClick={() => setSessionModalOpen(false)}>
                <LuX />
              </button>
            </div>
            <div className="session-analytics-body">
              <div className="session-period-controls">
                <label>View Period:</label>
                <select 
                  value={sessionPeriod} 
                  onChange={(e) => {
                    setSessionPeriod(e.target.value);
                  }}
                  className="session-period-dropdown"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
              
              <div className="session-metrics-grid">
                <div className="session-metric-card">
                  <div className="session-metric-value">
                    {sessionStats[selectedUserForSessions.id]?.totalHours?.toFixed(1) || '0.0'}
                  </div>
                  <div className="session-metric-label">Total Hours</div>
                </div>
                <div className="session-metric-card">
                  <div className="session-metric-value">
                    {userSessions[selectedUserForSessions.id]?.sessions?.length || 0}
                  </div>
                  <div className="session-metric-label">Sessions</div>
                </div>
                <div className="session-metric-card">
                  <div className="session-metric-value">
                    {userSessions[selectedUserForSessions.id]?.sessions?.filter(s => s.logout_time).length || 0}
                  </div>
                  <div className="session-metric-label">Completed</div>
                </div>
              </div>
              
              <div className="session-history-section">
                <h3>Recent Sessions ({sessionPeriod})</h3>
                {userSessions[selectedUserForSessions.id]?.sessions?.length > 0 ? (
                  <div className="session-history-table">
                    <div className="session-table-header-row">
                      <div>Login Time</div>
                      <div>Logout Time</div>
                      <div>Duration</div>
                      <div>Status</div>
                    </div>
                    {userSessions[selectedUserForSessions.id].sessions.map(session => (
                      <div key={session.id} className="session-table-data-row">
                        <div>
                          {session.login_time 
                            ? new Date(session.login_time).toLocaleString()
                            : 'N/A'
                          }
                        </div>
                        <div>
                          {session.logout_time 
                            ? new Date(session.logout_time).toLocaleString()
                            : 'Active'
                          }
                        </div>
                        <div className="session-duration-badge">
                          {session.session_duration_minutes 
                            ? `${Math.floor(session.session_duration_minutes / 60)}h ${session.session_duration_minutes % 60}m`
                            : session.logout_time ? '0m' : 'In Progress'
                          }
                        </div>
                        <div>
                          <span className={`session-status-indicator ${session.logout_time ? 'session-status-completed' : 'session-status-active'}`}>
                            {session.logout_time ? 'Completed' : 'Active'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="session-empty-state">
                    <p>No sessions found for the selected period.</p>
                    <p style={{fontSize: '12px', color: '#666', marginTop: '10px'}}>
                      Sessions will appear after users login and logout.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="user-management-modal-overlay">
          <div className="user-management-modal">
            <div className="user-management-modal-header">
              <h2 className="user-management-modal-title">Add New User</h2>
              <button className="user-management-close-button" onClick={closeAddModal}>
                <LuX />
              </button>
            </div>
            <div className="user-management-modal-body">
              <div className="user-management-form-group">
                <label className="user-management-label">First Name*</label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                  className="user-management-input"
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Last Name</label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                  className="user-management-input"
                  placeholder="Enter last name"
                />
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Email*</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  className="user-management-input"
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Mobile</label>
                <input
                  type="tel"
                  value={newUser.mobile}
                  onChange={(e) => setNewUser({...newUser, mobile: e.target.value})}
                  className="user-management-input"
                  placeholder="Enter mobile number"
                />
              </div>
              <div className="user-management-form-group" style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label className="user-management-label" style={{ margin: 0 }}>Role</label>
                  <span style={{ color: '#0eb381', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <LuSettings size={12} /> Click âš™ï¸ to set menu access
                  </span>
                </div>
                <div
                  className="user-management-custom-select-trigger"
                  onClick={() => setAddRoleDropdownOpen(!addRoleDropdownOpen)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {roles.find(r => r.value === newUser.role)?.label || newUser.role || 'User'}
                  </span>
                  <LuChevronDown
                    size={18}
                    style={{
                      transform: addRoleDropdownOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                      color: '#666'
                    }}
                  />
                </div>

                {addRoleDropdownOpen && (
                  <div className="user-management-custom-select-menu">
                    {roles.map(role => (
                      <div
                        key={role.value}
                        className={`user-management-custom-select-option ${newUser.role === role.value ? 'selected' : ''}`}
                        onClick={() => {
                          setNewUser({ ...newUser, role: role.value });
                          setAddRoleDropdownOpen(false);
                        }}
                      >
                        <span className="role-option-label">{role.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Password*</label>
                <div className="user-management-password-input-wrapper">
                  <input
                    type={showAddPassword ? "text" : "password"}
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="user-management-input"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    className="user-management-password-toggle"
                    onClick={() => setShowAddPassword(!showAddPassword)}
                  >
                    {showAddPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                  </button>
                </div>
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Employee ID (for Timesheets)</label>
                <input
                  type="text"
                  value={newUser.employeeId}
                  onChange={(e) => setNewUser({...newUser, employeeId: e.target.value})}
                  className="user-management-input"
                  placeholder="e.g. EMP7942"
                />
              </div>
            </div>
            <div className="user-management-modal-footer">
              <button className="user-management-cancel-button" onClick={closeAddModal}>
                Cancel
              </button>
              <button className="user-management-save-button" onClick={handleAddUser}>
                <LuSave />
                Add User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="user-management-modal-overlay">
          <div className="user-management-modal">
            <div className="user-management-modal-header">
              <h2 className="user-management-modal-title">Edit User</h2>
              <button className="user-management-close-button" onClick={closeEditModal}>
                <LuX />
              </button>
            </div>
            <div className="user-management-modal-body">
              <div className="user-management-form-group">
                <label className="user-management-label">First Name*</label>
                <input
                  type="text"
                  value={selectedUser.firstName || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, firstName: e.target.value})}
                  className="user-management-input"
                  placeholder="Enter first name"
                  required
                />
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Last Name</label>
                <input
                  type="text"
                  value={selectedUser.lastName || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, lastName: e.target.value})}
                  className="user-management-input"
                  placeholder="Enter last name"
                />
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Email*</label>
                <input
                  type="email"
                  value={selectedUser.email || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})}
                  className="user-management-input"
                  placeholder="Enter email address"
                  required
                />
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Mobile</label>
                <input
                  type="tel"
                  value={selectedUser.mobile || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, mobile: e.target.value})}
                  className="user-management-input"
                  placeholder="Enter mobile number"
                />
              </div>
              <div className="user-management-form-group" style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label className="user-management-label" style={{ margin: 0 }}>Role</label>
                </div>
                <div
                  className="user-management-custom-select-trigger"
                  onClick={() => setEditRoleDropdownOpen(!editRoleDropdownOpen)}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {roles.find(r => r.value === (selectedUser.role || 'user'))?.label || selectedUser.role || 'User'}
                  </span>
                  <LuChevronDown
                    size={18}
                    style={{
                      transform: editRoleDropdownOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                      color: '#666'
                    }}
                  />
                </div>

                {editRoleDropdownOpen && (
                  <div className="user-management-custom-select-menu">
                    {roles.map(role => (
                      <div
                        key={role.value}
                        className={`user-management-custom-select-option ${(selectedUser.role || 'user') === role.value ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedUser({ ...selectedUser, role: role.value });
                          setEditRoleDropdownOpen(false);
                        }}
                      >
                        <span className="role-option-label">{role.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Individual Candidate / User Menu Access Shortcut */}
              <div style={{
                margin: '10px 0 16px 0',
                padding: '10px 14px',
                borderRadius: '8px',
                background: '#f0fdf9',
                border: '1px solid #cceee2',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#0b7355', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <LuShield size={15} /> Individual Menu Access
                  </div>
                  <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>
                    Configure custom menus specifically for {selectedUser.firstName ? `${selectedUser.firstName} ${selectedUser.lastName || ''}`.trim() : selectedUser.username}
                  </div>
                </div>
                <button
                  type="button"
                  className="role-perm-btn primary"
                  style={{ padding: '6px 12px', fontSize: '12px', whiteSpace: 'nowrap' }}
                  onClick={() => handleOpenUserMenuPermissions(selectedUser)}
                >
                  <LuSettings size={13} style={{ marginRight: '4px' }} />
                  Set User Menus
                </button>
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Password (optional)</label>
                <div className="user-management-password-input-wrapper">
                  <input
                    type={showEditPassword ? "text" : "password"}
                    value={selectedUser.password || ''}
                    onChange={(e) => setSelectedUser({...selectedUser, password: e.target.value})}
                    className="user-management-input"
                    placeholder="Enter new password (leave blank to keep current)"
                  />
                  <button
                    type="button"
                    className="user-management-password-toggle"
                    onClick={() => setShowEditPassword(!showEditPassword)}
                  >
                    {showEditPassword ? <LuEyeOff size={18} /> : <LuEye size={18} />}
                  </button>
                </div>
                <small style={{color: '#666', fontSize: '12px', marginTop: '4px', display: 'block'}}>
                  Leave blank to keep the current password unchanged
                </small>
              </div>
              <div className="user-management-form-group">
                <label className="user-management-label">Employee ID (for Timesheets)</label>
                <input
                  type="text"
                  value={selectedUser.EmployeeId || selectedUser.employeeId || ''}
                  onChange={(e) => setSelectedUser({...selectedUser, EmployeeId: e.target.value, employeeId: e.target.value})}
                  className="user-management-input"
                  placeholder="e.g. EMP7942"
                />
              </div>
            </div>
            <div className="user-management-modal-footer">
              <button className="user-management-cancel-button" onClick={closeEditModal}>
                Cancel
              </button>
              <button className="user-management-save-button" onClick={handleEditUser}>
                <LuSave />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Menu Access Permissions Modal */}
      {permissionsModalOpen && selectedRoleForPerms && (
        <div className="user-management-modal-overlay role-permissions-modal-overlay">
          <div className="user-management-modal role-permissions-modal">
            <div className="user-management-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '38px',
                  height: '38px',
                  borderRadius: '10px',
                  backgroundColor: '#e6f7f2',
                  color: '#0eb381',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  <LuSettings />
                </div>
                <div>
                  <h2 className="user-management-modal-title" style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    Configure Menu Access: <span style={{ color: '#0eb381' }}>{selectedRoleForPerms.label}</span>
                    {selectedUserForPerms && (
                      <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: '#e6f7f2', color: '#0eb381', fontWeight: 600 }}>
                        Candidate / User Access
                      </span>
                    )}
                  </h2>
                  <p style={{ margin: '3px 0 0 0', fontSize: '12px', color: '#666' }}>
                    {selectedUserForPerms 
                      ? "Select which sidebar menus and submenus this user can view and access."
                      : "Select which sidebar menus and submenus this role can view and access."
                    }
                  </p>
                </div>
              </div>
              <button
                className="user-management-close-button"
                onClick={() => {
                  setPermissionsModalOpen(false);
                  setSelectedUserForPerms(null);
                }}
              >
                <LuX />
              </button>
            </div>

            {/* Quick Actions Toolbar */}
            <div className="role-permissions-toolbar">
              <div style={{ fontSize: '13px', color: '#4b5563', fontWeight: 500 }}>
                Selected: <strong style={{ color: '#0eb381' }}>{currentPermissions.length}</strong> menus
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  type="button"
                  className="role-perm-btn secondary"
                  onClick={handleSelectAllPermissions}
                >
                  Select All
                </button>
                <button
                  type="button"
                  className="role-perm-btn secondary"
                  onClick={handleDeselectAllPermissions}
                >
                  Deselect All
                </button>
                <button
                  type="button"
                  className="role-perm-btn secondary"
                  onClick={handleResetToDefaultPermissions}
                  title="Reset to default permissions for this role"
                >
                  <LuRotateCcw size={13} style={{ marginRight: '4px' }} />
                  Reset Defaults
                </button>
              </div>
            </div>

            {/* Modal Body - Grouped Checklist */}
            <div className="user-management-modal-body role-permissions-modal-body">
              {getActiveCatalog().map((cat, cIdx) => (
                <div key={cIdx} className="role-permissions-category-block">
                  <div className="role-permissions-category-title">
                    {cat.category}
                  </div>
                  <div className="role-permissions-grid">
                    {cat.items.map(item => {
                      const isChecked = currentPermissions.includes(item.key);
                      return (
                        <label
                          key={item.key}
                          className={`role-perm-item-card ${isChecked ? 'active' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleTogglePermission(item.key)}
                            className="role-perm-checkbox"
                          />
                          <div className="role-perm-item-info">
                            <span className="role-perm-item-label">{item.label}</span>
                            <span className="role-perm-item-desc">{item.desc}</span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="user-management-modal-footer">
              <button
                className="user-management-cancel-button"
                onClick={() => {
                  setPermissionsModalOpen(false);
                  setSelectedUserForPerms(null);
                }}
              >
                Cancel
              </button>
              <button
                className="user-management-save-button"
                onClick={handleSaveRolePermissions}
                disabled={savingPermissions}
              >
                <LuSave />
                {savingPermissions ? 'Saving...' : 'Save Permissions'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instant Feedback Toast Notification */}
      {toastMessage && (
        <div className="user-management-toast">
          <LuCheck size={18} />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Click outside to close dropdown */}
      {dropdownOpen && (
        <div
          className="user-management-dropdown-overlay"
          onClick={() => setDropdownOpen(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;